<template>
  <div class="tracking-page">
    <div class="tracking-logo">
      <a href="/">
        <img :src="logoUrl" alt="SMASHING SHAPE" />
      </a>
    </div>

    <div class="container">
      <div class="tracking-header">
        <h1><i class="fas fa-shipping-fast"></i> Track Your Order</h1>
        <p>Enter your order number and email to track your order status</p>
      </div>

      <div class="tracking-form-card">
        <form @submit.prevent="trackOrder" class="tracking-form">
          <div class="form-row">
            <div class="form-group">
              <label>Order Number *</label>
              <input
                v-model="orderNumber"
                type="text"
                placeholder="e.g. ORD-123456"
                required
              >
            </div>
            <div class="form-group">
              <label>Email Address *</label>
              <input
                v-model="email"
                type="email"
                placeholder="Email used for order"
                required
              >
            </div>
          </div>
          <button type="submit" class="btn-track" :disabled="loading">
            <i v-if="loading" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-search"></i>
            <span>{{ loading ? 'Searching...' : 'Track Order' }}</span>
          </button>
        </form>
      </div>

      <!-- Tracking Result -->
      <div v-if="order" class="tracking-result">
        <div class="result-card">
          <div class="result-header">
            <h2>Order #{{ order.number }}</h2>
            <span :class="['status-badge', 'status-' + order.status]">{{ formatStatus(order.status) }}</span>
          </div>

          <!-- Order Status Timeline -->
          <div v-if="order.status !== 'cancelled'" class="status-timeline">
            <div
              v-for="(step, index) in timelineSteps"
              :key="step.status"
              class="timeline-item"
              :class="{ active: isStepActive(step.status, index) }"
            >
              <div class="timeline-dot"></div>
              <span>{{ step.label }}</span>
            </div>
          </div>

          <!-- Cancelled Timeline -->
          <div v-else class="status-timeline cancelled">
            <div class="timeline-item active cancelled">
              <div class="timeline-dot"></div>
              <span>Order Cancelled</span>
            </div>
          </div>

          <div class="result-details">
            <div class="detail-item">
              <i class="fas fa-calendar-alt"></i>
              <div>
                <span class="label">Order Date</span>
                <span class="value">{{ formatDate(order.created_at) }}</span>
              </div>
            </div>

            <div v-if="order.shipped_at" class="detail-item">
              <i class="fas fa-truck"></i>
              <div>
                <span class="label">Shipped Date</span>
                <span class="value">{{ formatDate(order.shipped_at) }}</span>
              </div>
            </div>

            <div v-if="order.tracking_number" class="detail-item">
              <i class="fas fa-barcode"></i>
              <div>
                <span class="label">Tracking Number</span>
                <span class="value tracking-number">{{ order.tracking_number }}</span>
              </div>
            </div>

            <div v-if="order.shipping_carrier" class="detail-item">
              <i class="fas fa-building"></i>
              <div>
                <span class="label">Carrier</span>
                <span class="value">{{ order.shipping_carrier }}</span>
              </div>
            </div>

            <div v-if="order.estimated_delivery_date" class="detail-item">
              <i class="fas fa-clock"></i>
              <div>
                <span class="label">Estimated Delivery</span>
                <span class="value">{{ formatDate(order.estimated_delivery_date) }}</span>
              </div>
            </div>

            <div v-if="order.delivered_at" class="detail-item success">
              <i class="fas fa-check-circle"></i>
              <div>
                <span class="label">Delivered</span>
                <span class="value">{{ formatDate(order.delivered_at) }}</span>
              </div>
            </div>

            <div class="detail-item">
              <i class="fas fa-map-marker-alt"></i>
              <div>
                <span class="label">Shipping To</span>
                <span class="value">{{ order.shipping_city }}{{ order.shipping_state ? ', ' + order.shipping_state : '' }}</span>
              </div>
            </div>

            <div class="detail-item">
              <i class="fas fa-shopping-bag"></i>
              <div>
                <span class="label">Items</span>
                <span class="value">{{ order.items_count }} item(s)</span>
              </div>
            </div>

            <div class="detail-item">
              <i class="fas fa-naira-sign"></i>
              <div>
                <span class="label">Total</span>
                <span class="value total">₦{{ formatPrice(order.total) }}</span>
              </div>
            </div>
          </div>

          <!-- Order Items -->
          <div class="order-items-section">
            <h3>Order Items</h3>
            <div class="order-items">
              <div v-for="item in order.order_items" :key="item.product_name" class="order-item">
                <img :src="item.image || '/images/placeholder.jpg'" :alt="item.product_name">
                <div class="item-details">
                  <div class="item-name">{{ item.product_name }}</div>
                  <div class="item-meta">Qty: {{ item.quantity }}</div>
                </div>
                <div class="item-price">₦{{ formatPrice(item.price * item.quantity) }}</div>
              </div>
            </div>
          </div>

          <div class="result-actions">
            <a href="/help-center" class="btn-secondary">
              <i class="fas fa-question-circle"></i> Need Help?
            </a>
            <a href="/contact" class="btn-primary">
              <i class="fas fa-envelope"></i> Contact Support
            </a>
          </div>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="tracking-error">
        <i class="fas fa-exclamation-circle"></i>
        <p>{{ error }}</p>
        <button type="button" class="btn-try-again" @click="error = null">
          Try Again
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Tracking',
  data() {
    return {
      logoUrl: '/logos/logodark.svg',
      orderNumber: '',
      email: '',
      loading: false,
      order: null,
      error: null,
      timelineSteps: [
        { status: 'pending', label: 'Order Placed' },
        { status: 'paid', label: 'Processing' },
        { status: 'shipped', label: 'Shipped' },
        { status: 'delivered', label: 'Delivered' }
      ]
    }
  },
  methods: {
    async trackOrder() {
      this.loading = true
      this.order = null
      this.error = null

      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
        const response = await fetch('/track', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-TOKEN': csrfToken
          },
          body: JSON.stringify({
            order_number: this.orderNumber,
            email: this.email
          })
        })

        const data = await response.json()

        if (response.ok && data.success) {
          this.order = data.order
          this.$nextTick(() => {
            document.querySelector('.tracking-result')?.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          })
        } else {
          this.error = data.message || 'Order not found'
        }
      } catch (err) {
        this.error = 'An error occurred. Please try again.'
      } finally {
        this.loading = false
      }
    },

    isStepActive(status, index) {
      if (!this.order) return false

      const statusOrder = ['pending', 'paid', 'shipped', 'delivered']
      let currentIndex = statusOrder.indexOf(this.order.status)

      // Handle processing as paid
      if (this.order.status === 'processing') {
        currentIndex = statusOrder.indexOf('paid')
      }

      return index <= currentIndex
    },

    formatStatus(status) {
      const statusMap = {
        'pending': 'Order Placed',
        'paid': 'Processing',
        'processing': 'Processing',
        'shipped': 'Shipped',
        'delivered': 'Delivered',
        'cancelled': 'Cancelled'
      }
      return statusMap[status] || status
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-NG', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },

    formatPrice(price) {
      return new Intl.NumberFormat('en-NG').format(price || 0)
    }
  }
}
</script>
